import React, { createContext, useContext, useState } from 'react';
import { storefrontService } from '../services/storefront';
import { trackEvent } from '../lib/posthog';

const ShopContext = createContext();

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) throw new Error('useShop must be used within a ShopProvider');
  return context;
};

export const ShopProvider = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const openQuickView = (product) => { setSelectedProduct(product); setIsQuickViewOpen(true); };
  const closeQuickView = () => setIsQuickViewOpen(false);

  const addToCart = (product) => {
    trackEvent('add_to_cart', {
      product_id: product.id,
      product_title: product.title,
      product_price: product.price,
    });
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev =>
      prev
        .map(item =>
          item.id === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  };

  const proceedToCheckout = async () => {
    if (cart.length === 0) return;
    setIsCheckingOut(true);
    trackEvent('checkout_started', {
      cart_size: cart.length,
      cart_total: cartTotal,
    });
    try {
      const checkoutUrl = await storefrontService.createCheckoutUrl(cart);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Kasse-feil:', err.message);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const cartTotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <ShopContext.Provider value={{
      isCartOpen, openCart, closeCart,
      cart, addToCart, removeFromCart, updateQuantity,
      cartTotal, cartCount,
      isCheckingOut, proceedToCheckout,
      selectedProduct, isQuickViewOpen, openQuickView, closeQuickView,
    }}>
      {children}
    </ShopContext.Provider>
  );
};
